import {
  isReleaseSearchFilterEmpty,
  type ReleaseSearchFilter,
  type StringMatcher,
} from "@garimpa/lib/searchState";

function unique(values: readonly string[]): string[] {
  return [...new Set(values)];
}

function matcherValues(matcher: StringMatcher | undefined): string[] {
  if (!matcher) return [];
  if (typeof matcher === "string") return matcher ? [matcher] : [];
  if (Array.isArray(matcher)) return matcher;
  return matcher.all;
}

function getMatcher(
  filter: ReleaseSearchFilter | undefined,
  key: string,
): StringMatcher | undefined {
  if (!filter) return undefined;
  if (key === "artist.country") return filter.artist?.country;
  if (key === "year" || key === "OR") return undefined;
  return filter[key as keyof Omit<ReleaseSearchFilter, "artist" | "year" | "OR">] as
    | StringMatcher
    | undefined;
}

function setMatcher(filter: ReleaseSearchFilter, key: string, matcher: StringMatcher | undefined) {
  if (key === "artist.country") {
    if (matcher) filter.artist = { ...filter.artist, country: matcher };
    else if (filter.artist) {
      const nextArtist = { ...filter.artist };
      delete nextArtist.country;
      filter.artist = Object.keys(nextArtist).length ? nextArtist : undefined;
    }
    return;
  }

  if (key === "year" || key === "OR") return;
  if (matcher) filter[key as keyof Omit<ReleaseSearchFilter, "artist" | "year" | "OR">] = matcher;
  else delete filter[key as keyof Omit<ReleaseSearchFilter, "artist" | "year" | "OR">];
}

function matcherWithoutValues(
  matcher: StringMatcher | undefined,
  removedValues: ReadonlySet<string>,
): StringMatcher | undefined {
  if (!matcher) return undefined;
  const values = matcherValues(matcher).filter((value) => !removedValues.has(value));
  if (!values.length) return undefined;
  if (
    typeof matcher === "object" &&
    !Array.isArray(matcher) &&
    "all" in matcher &&
    values.length > 1
  ) {
    return { all: values };
  }
  return values.length === 1 ? values[0]! : values;
}

function equivalentOrKeys(key: string): readonly string[] {
  return key === "label" || key === "company" ? ["label", "company"] : [key];
}

function removeFilterValues(
  filter: ReleaseSearchFilter,
  keys: readonly string[],
  removedValues: ReadonlySet<string>,
): ReleaseSearchFilter | undefined {
  const next: ReleaseSearchFilter = {
    ...filter,
    artist: filter.artist ? { ...filter.artist } : undefined,
    OR: undefined,
  };

  for (const key of keys) {
    setMatcher(next, key, matcherWithoutValues(getMatcher(next, key), removedValues));
  }

  if (Array.isArray(filter.OR)) {
    const OR = filter.OR.map((child) => removeFilterValues(child, keys, removedValues)).filter(
      (child): child is ReleaseSearchFilter => !isReleaseSearchFilterEmpty(child),
    );
    if (OR.length) next.OR = OR;
  }

  return isReleaseSearchFilterEmpty(next) ? undefined : next;
}

function getOrValues(
  filter: ReleaseSearchFilter | undefined,
  key: string,
  availableValues: ReadonlySet<string> | undefined,
): string[] {
  if (!filter || !availableValues || !Array.isArray(filter.OR)) return [];

  return filter.OR.flatMap((child) => [
    ...matcherValues(getMatcher(child, key)).filter((value) => availableValues.has(value)),
    ...getOrValues(child, key, availableValues),
  ]);
}

export function getReleaseSearchFilterValues(
  filter: ReleaseSearchFilter | undefined,
  key: string,
  availableValues?: readonly string[],
): string[] {
  const directValues = matcherValues(getMatcher(filter, key));
  const orValues = getOrValues(filter, key, availableValues ? new Set(availableValues) : undefined);
  return unique([...directValues, ...orValues]);
}

export function removeOrFilterValues(
  filter: ReleaseSearchFilter,
  key: string,
  values: readonly string[],
): ReleaseSearchFilter {
  if (!values.length || !Array.isArray(filter.OR)) return filter;
  const removedValues = new Set(values);
  const OR = filter.OR.map((child) =>
    removeFilterValues(child, equivalentOrKeys(key), removedValues),
  ).filter((child): child is ReleaseSearchFilter => !isReleaseSearchFilterEmpty(child));

  return {
    ...filter,
    OR: OR.length ? OR : undefined,
  };
}
